import { useCallback, useEffect, useState } from "react";
import { Gift, Loader2, Search, ShieldOff, Trash2, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Card, Stat } from "./_shared";

export function SubscriptionsTab() {
  const { user, apiFetch } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [granting, setGranting] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [disabling2fa, setDisabling2fa] = useState(null);
  const isOwner = user?.role === "owner";

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await apiFetch(`/api/admin/subscriptions${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(d.items || []);
    } catch { setItems([]); }
  }, [apiFetch, q]);

  useEffect(() => { load(); }, [load]);

  const counts = (items || []).reduce((acc, u) => {
    const s = u.access?.state || "expired";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-px">
      <Card title="SUBSCRIPTIONS"
            subtitle="All consumer accounts (role=subscriber). Grant free months bypasses Stripe — useful for friends, beta testers, refunds, support compensation."
            accent="#FF8A5C" testid="subscriptions-card">

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
          <Stat label="TOTAL"    value={items?.length ?? "—"} accent="#FF8A5C" />
          <Stat label="TRIALING" value={counts.trialing || 0} accent="#6FB6F2" />
          <Stat label="ACTIVE"   value={counts.active || 0}   accent="#10B981" />
          <Stat label="GRANTED"  value={counts.granted || 0}  accent="#FF8A5C" />
          <Stat label="EXPIRED"  value={(counts.expired||0)+(counts.canceled||0)+(counts.past_due||0)} accent="#EF4444" />
        </div>

        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="w-3.5 h-3.5 text-[var(--srs-ink-faint)] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email…"
              data-testid="sub-search"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#FF8A5C] outline-none pl-9 pr-4 py-2.5 text-xs text-[var(--srs-ink)] font-mono placeholder:text-[var(--srs-ink-faint)] transition-colors"
            />
          </div>
          <button onClick={load}
            className="px-4 py-2.5 border border-[var(--srs-line)] hover:border-[#FF8A5C] text-[10px] tracking-[0.2em] font-bold uppercase text-[var(--srs-ink-dim)] hover:text-[#FF8A5C] transition-colors"
            data-testid="sub-reload-btn">
            Reload
          </button>
        </div>

        {items === null && (
          <div className="text-xs text-[var(--srs-ink-faint)] flex items-center gap-2 py-8 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}

        {items?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center">No subscribers yet.</div>
        )}

        <div className="flex flex-col gap-2" data-testid="subscriptions-list">
          {items?.map((u) => {
            const a = u.access || {};
            const tone = STATE_TONES[a.state] || STATE_TONES.expired;
            return (
              <div key={u.id} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`sub-row-${u.email}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--srs-ink)] truncate" data-testid={`sub-email-${u.email}`}>{u.email}</span>
                    <span
                      className="px-2 py-0.5 text-[9px] tracking-[0.22em] font-bold uppercase"
                      style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
                      data-testid={`sub-state-${u.email}`}>
                      {tone.label}
                    </span>
                    {a.days_left != null && (
                      <span className="px-2 py-0.5 text-[9px] tracking-[0.22em] font-bold uppercase bg-[var(--srs-surface-2)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">
                        {a.days_left}d igjen
                      </span>
                    )}
                    {u.two_factor_enabled && (
                      <span
                        className="px-2 py-0.5 text-[9px] tracking-[0.22em] font-bold uppercase"
                        style={{ background: "rgba(111,182,242,0.10)", color: "#6FB6F2", border: "1px solid rgba(111,182,242,0.35)" }}
                        data-testid={`sub-2fa-on-${u.email}`}>
                        2FA ON
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-[var(--srs-ink-faint)] tracking-wide flex flex-wrap gap-3">
                    {u.name && <span>{u.name}</span>}
                    {u.created_at && <span>opprettet {new Date(u.created_at).toLocaleDateString()}</span>}
                    {a.trial_ends_at && <span>trial→ {new Date(a.trial_ends_at).toLocaleDateString()}</span>}
                    {a.granted_until && <span style={{ color: "#FF8A5C" }}>granted→ {new Date(a.granted_until).toLocaleDateString()}</span>}
                    {a.current_period_end && <span>stripe→ {new Date(a.current_period_end).toLocaleDateString()}</span>}
                    {a.subscription_status && <span>stripe-status: {a.subscription_status}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setGranting(u)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#FF8A5C] hover:text-[#FF8A5C] text-[var(--srs-ink-dim)] transition-colors"
                    data-testid={`sub-grant-${u.email}`}>
                    <Gift className="w-3 h-3" /> Grant months
                  </button>
                  {a.granted_until && new Date(a.granted_until) > new Date() && (
                    <button onClick={() => setRevoking(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#EF4444] hover:text-[#EF4444] text-[var(--srs-ink-dim)] transition-colors"
                      data-testid={`sub-revoke-${u.email}`}>
                      <X className="w-3 h-3" /> Revoke
                    </button>
                  )}
                  {isOwner && u.two_factor_enabled && (
                    <button onClick={() => setDisabling2fa(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#F59E0B] hover:text-[#F59E0B] text-[var(--srs-ink-dim)] transition-colors"
                      data-testid={`sub-disable-2fa-${u.email}`}
                      title="Disable email-code 2FA on this account (support / CWS reviewer access)">
                      <ShieldOff className="w-3 h-3" /> Disable 2FA
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={() => setDeleting(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#DC2626] hover:text-[#DC2626] text-[var(--srs-ink-dim)] transition-colors"
                      data-testid={`sub-delete-${u.email}`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {granting && (
        <GrantMonthsModal user={granting} onClose={() => setGranting(null)} onDone={() => { setGranting(null); load(); }} />
      )}
      {revoking && (
        <RevokeMonthsModal user={revoking} onClose={() => setRevoking(null)} onDone={() => { setRevoking(null); load(); }} />
      )}
      {deleting && (
        <DeleteUserModal user={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />
      )}
      {disabling2fa && (
        <Disable2FAModal user={disabling2fa} onClose={() => setDisabling2fa(null)} onDone={() => { setDisabling2fa(null); load(); }} />
      )}
    </div>
  );
}

export const STATE_TONES = {
  trialing: { label: "TRIALING", bg: "rgba(111,182,242,0.10)", fg: "#6FB6F2", border: "rgba(111,182,242,0.35)" },
  active:   { label: "ACTIVE",   bg: "rgba(16,185,129,0.10)",  fg: "#10B981", border: "rgba(16,185,129,0.30)" },
  granted:  { label: "GRANTED",  bg: "rgba(255,138,92,0.12)",  fg: "#FF8A5C", border: "rgba(255,138,92,0.35)" },
  past_due: { label: "PAST DUE", bg: "rgba(245,158,11,0.12)",  fg: "#F59E0B", border: "rgba(245,158,11,0.35)" },
  canceled: { label: "CANCELED", bg: "rgba(82,82,91,0.18)",    fg: "#A1A1AA", border: "rgba(82,82,91,0.40)" },
  expired:  { label: "EXPIRED",  bg: "rgba(239,68,68,0.10)",   fg: "#EF4444", border: "rgba(239,68,68,0.30)" },
  admin:    { label: "ADMIN",    bg: "rgba(34,211,238,0.10)",  fg: "#22D3EE", border: "rgba(34,211,238,0.30)" },
};


export function GrantMonthsModal({ user, onClose, onDone }) {
  const { apiFetch } = useAuth();
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await apiFetch("/api/admin/grant-months", {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, months: Number(months), reason }),
      });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="grant-modal">
      <form onSubmit={submit} className="w-full max-w-[440px] border border-[var(--srs-line)] bg-[var(--srs-surface)] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-[#FF8A5C]">GRANT FREE MONTHS</div>
          <button type="button" onClick={onClose} className="text-[var(--srs-ink-faint)] hover:text-[var(--srs-ink)]" data-testid="grant-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mb-4 text-xs text-[var(--srs-ink-dim)]">
          <div className="font-mono text-[var(--srs-ink)]">{user.email}</div>
          <div className="text-[var(--srs-ink-faint)] mt-1">Adds the months to <strong className="text-[#FF8A5C]">granted_until</strong> — independent of Stripe state. Can stack.</div>
        </div>

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">MONTHS (1–60)</label>
        <input type="number" min={1} max={60} required value={months} onChange={(e) => setMonths(e.target.value)}
          data-testid="grant-months-input"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#FF8A5C] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono mb-4" />

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">REASON (audit log)</label>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="grant-reason-input"
          placeholder="VIP, refund, beta tester…"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#FF8A5C] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] mb-4" />

        {err && <div className="text-xs text-[#EF4444] mb-3" data-testid="grant-error">{err}</div>}

        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:border-[#52525B]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="grant-submit"
            className="px-5 py-2 bg-[#FF8A5C] hover:bg-[#ff6e3d] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black text-[10px] tracking-[0.25em] font-bold uppercase transition-colors">
            {busy ? "Granting…" : `Grant ${months} month${Number(months) === 1 ? "" : "s"}`}
          </button>
        </div>
      </form>
    </div>
  );
}


export function RevokeMonthsModal({ user, onClose, onDone }) {
  const { apiFetch } = useAuth();
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Show how many months are still active so the admin doesn't accidentally
  // revoke more than what's left.
  const grantedUntil = user.access?.granted_until || user.granted_until;
  const remainingDays = grantedUntil ? Math.max(0, Math.ceil((new Date(grantedUntil) - new Date()) / 86400000)) : 0;
  const remainingMonths = Math.max(1, Math.ceil(remainingDays / 30));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await apiFetch("/api/admin/revoke-months", {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, months: Number(months), reason }),
      });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="revoke-modal">
      <form onSubmit={submit} className="w-full max-w-[440px] border border-[var(--srs-line)] bg-[var(--srs-surface)] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-[#EF4444]">REVOKE FREE MONTHS</div>
          <button type="button" onClick={onClose} className="text-[var(--srs-ink-faint)] hover:text-[var(--srs-ink)]" data-testid="revoke-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mb-4 text-xs text-[var(--srs-ink-dim)]">
          <div className="font-mono text-[var(--srs-ink)]">{user.email}</div>
          <div className="text-[var(--srs-ink-faint)] mt-1">
            Subtracts the months from <strong className="text-[#FF8A5C]">granted_until</strong>. If the resulting date is in the past, free access is cleared entirely. Recorded in audit log + grant history with a negative-month entry.
          </div>
          {grantedUntil && (
            <div className="mt-2 p-2 bg-[var(--srs-surface-2)] rounded text-[10px] font-mono">
              Currently granted until: {new Date(grantedUntil).toLocaleDateString()} (≈{remainingMonths} {remainingMonths === 1 ? "month" : "months"} / {remainingDays}d left)
            </div>
          )}
        </div>

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">MONTHS TO REVOKE (1–{Math.min(60, remainingMonths)})</label>
        <input type="number" min={1} max={Math.min(60, remainingMonths)} required value={months} onChange={(e) => setMonths(e.target.value)}
          data-testid="revoke-months-input"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#EF4444] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono mb-4" />

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">REASON (audit log)</label>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="revoke-reason-input"
          placeholder="Granted by mistake, abuse, refund chargeback…"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#EF4444] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] mb-4" />

        {err && <div className="text-xs text-[#EF4444] mb-3" data-testid="revoke-error">{err}</div>}

        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:border-[#52525B]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="revoke-submit"
            className="px-5 py-2 bg-[#EF4444] hover:bg-[#dc2626] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-white text-[10px] tracking-[0.25em] font-bold uppercase transition-colors">
            {busy ? "Revoking…" : `Revoke ${months} month${Number(months) === 1 ? "" : "s"}`}
          </button>
        </div>
      </form>
    </div>
  );
}


// Owner-only. Hard-deletes a subscriber account; optionally bans the email to block re-signup.
// Two-step confirmation (must type the email) so the action isn't a one-tap mistake.
export function DeleteUserModal({ user, onClose, onDone }) {
  const { apiFetch } = useAuth();
  const [confirmEmail, setConfirmEmail] = useState("");
  const [banEmail, setBanEmail] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canSubmit = confirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase();

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) { setErr("Email confirmation does not match."); return; }
    setBusy(true); setErr("");
    try {
      await apiFetch("/api/admin/users/delete", {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, reason, ban_email: banEmail }),
      });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="delete-user-modal">
      <form onSubmit={submit} className="w-full max-w-[460px] border border-[#DC2626]/40 bg-[var(--srs-surface)] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-[#DC2626]">DELETE USER</div>
          <button type="button" onClick={onClose} className="text-[var(--srs-ink-faint)] hover:text-[var(--srs-ink)]" data-testid="delete-user-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mb-4 text-xs text-[var(--srs-ink-dim)]">
          <div className="font-mono text-[var(--srs-ink)]">{user.email}</div>
          <div className="text-[var(--srs-ink-faint)] mt-1">
            Permanently removes the account, cancels any active Stripe subscription, and wipes device-session tracking. Audit log entries are kept for compliance.
          </div>
        </div>

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">
          TYPE THE EMAIL TO CONFIRM
        </label>
        <input
          type="text"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={user.email}
          autoComplete="off"
          data-testid="delete-user-confirm-input"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#DC2626] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono mb-4"
        />

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">REASON (audit log)</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Spam, abuse, GDPR erasure request…"
          data-testid="delete-user-reason-input"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#DC2626] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] mb-4"
        />

        <label className="flex items-start gap-2 cursor-pointer mb-4 select-none" data-testid="delete-user-ban-label">
          <input
            type="checkbox"
            checked={banEmail}
            onChange={(e) => setBanEmail(e.target.checked)}
            data-testid="delete-user-ban-checkbox"
            className="mt-1 accent-[#DC2626]"
          />
          <span className="text-xs text-[var(--srs-ink-dim)]">
            <strong className="text-[#DC2626]">Also ban this email</strong> — blocks the same address from ever signing up again. Without this, the user can re-register with the same email.
          </span>
        </label>

        {err && <div className="text-xs text-[#EF4444] mb-3" data-testid="delete-user-error">{err}</div>}

        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:border-[#52525B]">Cancel</button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            data-testid="delete-user-submit"
            className="px-5 py-2 bg-[#DC2626] hover:bg-[#b91c1c] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-white text-[10px] tracking-[0.25em] font-bold uppercase transition-colors"
          >
            {busy ? "Deleting…" : banEmail ? "Delete + Ban" : "Delete account"}
          </button>
        </div>
      </form>
    </div>
  );
}


// Owner-only. Force-disables email-code 2FA on a user. Use case: Chrome
// Web Store reviewer accounts where the reviewer can't receive 2FA codes,
// or support tickets where the user lost access to their email.
export function Disable2FAModal({ user, onClose, onDone }) {
  const { apiFetch } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await apiFetch("/api/admin/users/disable-2fa", {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, reason }),
      });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="disable-2fa-modal">
      <form onSubmit={submit} className="w-full max-w-[460px] border border-[var(--srs-line)] bg-[var(--srs-surface)] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-[#F59E0B]">DISABLE 2FA</div>
          <button type="button" onClick={onClose} className="text-[var(--srs-ink-faint)] hover:text-[var(--srs-ink)]" data-testid="disable-2fa-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mb-4 text-xs text-[var(--srs-ink-dim)]">
          <div className="font-mono text-[var(--srs-ink)]">{user.email}</div>
          <div className="text-[var(--srs-ink-faint)] mt-1">
            Turns OFF email-code 2FA and clears every trusted device for this user. They&apos;ll be able to log in with just email + password — without receiving a code. Useful for Chrome Web Store reviewer accounts and support overrides.
          </div>
          <div className="mt-2 p-2 bg-[var(--srs-surface-2)] rounded text-[10px] font-mono text-[#F59E0B]">
            The user can re-enable 2FA from their own Account → Security page at any time.
          </div>
          <div className="mt-2 p-2 bg-[var(--srs-surface-2)] rounded text-[10px] font-mono text-[var(--srs-ink-dim)]">
            🔔 <strong>A security-notification email is sent to the user</strong> when this action runs (if their address is verified). The reason you enter below appears in that email.
          </div>
        </div>

        <label className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">REASON (audit log)</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Chrome Web Store reviewer, lost-access support, …"
          autoComplete="off"
          data-testid="disable-2fa-reason-input"
          className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#F59E0B] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] mb-4"
        />

        {err && <div className="text-xs text-[#EF4444] mb-3" data-testid="disable-2fa-error">{err}</div>}

        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:border-[#52525B]">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="disable-2fa-submit"
            className="px-5 py-2 bg-[#F59E0B] hover:bg-[#d97706] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black text-[10px] tracking-[0.25em] font-bold uppercase transition-colors"
          >
            {busy ? "Disabling…" : "Disable 2FA"}
          </button>
        </div>
      </form>
    </div>
  );
}

